import React, { FC, useMemo } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Button, Form } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { Spinner } from 'react-bootstrap';
import useCreateAccount from '../../hooks/api/users/useCreateAccount';
import { useHistory } from 'react-router';
import { isEmailExists } from '../../services/user/UserService';

interface Props {
  setError: (e: string) => void;
  invitationId: string | undefined;
}

const KreezeeCreateAccount: FC<Props> = ({ setError, invitationId }: Props) => {
  const intl = useIntl();
  const { createAccount, isLoading } = useCreateAccount();
  const history = useHistory();

  const CreateAccountSchema = useMemo(
    () =>
      Yup.object().shape({
        firstName: Yup.string()
          .min(2, intl.formatMessage({ id: 'loginPage.firstNameLengthError' }))
          .max(50, intl.formatMessage({ id: 'loginPage.firstNameLengthError' }))
          .required(intl.formatMessage({ id: 'loginPage.firstNameError' })),
        lastName: Yup.string()
          .min(2, intl.formatMessage({ id: 'loginPage.lastNameLengthError' }))
          .max(50, intl.formatMessage({ id: 'loginPage.lastNameLengthError' }))
          .required(intl.formatMessage({ id: 'loginPage.lastNameError' })),
        email: Yup.string()
          .email(intl.formatMessage({ id: 'loginPage.emailValidError' }))
          .required(intl.formatMessage({ id: 'loginPage.emailError' }))
          .test('checkEmailExists', intl.formatMessage({ id: 'loginPage.emailExistsError' }), async (email) => {
            if (email) {
              const response = await isEmailExists(email);
              return !response.data;
            }

            return true;
          }),
        password: Yup.string()
          .min(4, intl.formatMessage({ id: 'loginPage.passwordLengthError' }))
          .required(intl.formatMessage({ id: 'loginPage.passwordError' })),
        confirmPassword: Yup.string()
          .required(intl.formatMessage({ id: 'loginPage.confirmPasswordRequiredError' }))
          .oneOf([Yup.ref('password'), null], intl.formatMessage({ id: 'loginPage.passwordMustMatchError' })),
      }),
    [],
  );

  const handleCreateAccount = async (email, password, firstname, name) => {
    setError('');
    const result = await createAccount({
      email,
      password,
      name,
      firstname,
      invitationId,
      culture: intl.locale,
    });

    if (result) {
      history.push('/account-created');
    }
  };

  return (
    <React.Fragment>
      <Formik
        initialValues={{
          email: '',
          password: '',
          firstName: '',
          lastName: '',
          confirmPassword: '',
        }}
        validationSchema={CreateAccountSchema}
        onSubmit={(values) => handleCreateAccount(values.email, values.password, values.firstName, values.lastName)}
      >
        {({ errors, touched, values, handleChange, handleBlur, handleSubmit }) => {
          return (
            <Form className="kreezee-login" noValidate onSubmit={handleSubmit}>
              <div className="form-group">
                <Form.Label>{intl.formatMessage({ id: 'loginPage.firstName' })}</Form.Label>
                <Form.Control
                  disabled={isLoading}
                  type="text"
                  name="firstName"
                  placeholder={intl.formatMessage({ id: 'loginPage.firstName' })}
                  isInvalid={touched.firstName && !!errors.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.firstName}
                />
                <Form.Control.Feedback type="invalid">
                  *{errors.firstName && <span>{errors.firstName}</span>}
                </Form.Control.Feedback>
              </div>
              <div className="form-group">
                <Form.Label>{intl.formatMessage({ id: 'loginPage.lastName' })}</Form.Label>
                <Form.Control
                  disabled={isLoading}
                  type="text"
                  name="lastName"
                  placeholder={intl.formatMessage({ id: 'loginPage.lastName' })}
                  isInvalid={touched.lastName && !!errors.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.lastName}
                />
                <Form.Control.Feedback type="invalid">
                  *{errors.lastName && <span>{errors.lastName}</span>}
                </Form.Control.Feedback>
              </div>
              <div className="form-group">
                <Form.Label>{intl.formatMessage({ id: 'loginPage.email' })}</Form.Label>
                <Form.Control
                  disabled={isLoading}
                  type="email"
                  name="email"
                  placeholder={intl.formatMessage({ id: 'loginPage.email' })}
                  isInvalid={touched.email && !!errors.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  style={{ background: 'white' }}
                />
                <Form.Control.Feedback type="invalid">
                  *{errors.email && <span>{errors.email}</span>}
                </Form.Control.Feedback>
              </div>
              <div className="form-group">
                <Form.Label>{intl.formatMessage({ id: 'loginPage.password' })}</Form.Label>
                <Form.Control
                  disabled={isLoading}
                  type="password"
                  name="password"
                  placeholder={intl.formatMessage({ id: 'loginPage.password' })}
                  isInvalid={touched.password && !!errors.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.password}
                />
                <Form.Control.Feedback type="invalid">
                  *{errors.password && <span>{errors.password}</span>}
                </Form.Control.Feedback>
              </div>
              <div className="form-group">
                <Form.Label>{intl.formatMessage({ id: 'loginPage.confirmPassword' })}</Form.Label>
                <Form.Control
                  disabled={isLoading}
                  type="password"
                  name="confirmPassword"
                  placeholder={intl.formatMessage({ id: 'loginPage.confirmPassword' })}
                  isInvalid={touched.confirmPassword && !!errors.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.confirmPassword}
                />
                <Form.Control.Feedback type="invalid">
                  *{errors.confirmPassword && <span>{errors.confirmPassword}</span>}
                </Form.Control.Feedback>
              </div>

              <Button disabled={isLoading} variant="primary" type="submit" className="submit-button">
                {!isLoading && (
                  <div className="d-flex justify-content-center align-items-center">
                    {intl.formatMessage({ id: 'loginPage.createAccount' })}
                  </div>
                )}
                {isLoading && <Spinner size="sm" animation="border" />}
              </Button>
            </Form>
          );
        }}
      </Formik>
    </React.Fragment>
  );
};

export default KreezeeCreateAccount;
